import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
} from '@tanstack/react-query';
import { losslessJsonMiddleware } from '@/api/interceptors/wretch/lossless-json';
import { createQueryKey } from '@/api/query-keys';
import { type LosslessJson, stringifyLosslessJson } from '@/helpers/lossless-json';
import { type SpaceIds } from './space';
import {
  collectionUrl,
  restResult,
  spaceClient,
  type SpaceCollectionParams,
  spaceCollectionsKey,
} from './space-collections';

/*
 * Points. Reads are served by the search engine. Writes go through the ingest service, which answers once the
 * write is queued unless asked to wait for the updater to apply it.
 */

// The page size of the self-hosted web UI: one card per point.
const POINTS_PAGE_SIZE = 10;

/** A point id: an unsigned integer or a UUID. An integer past 2^53 is a `bigint`, kept exact. */
export type PointId = number | bigint | string;

export type SpacePointPayload = Record<string, LosslessJson>;

export type SpacePoint = {
  id: PointId;
  payload?: SpacePointPayload | null;
};

/** One vector of a point: dense, multivector, or sparse. */
type SpaceVector = number[] | number[][] | { indices: number[]; values: number[] };

/** A point's vectors: the unnamed vector bare when it is the only one, else a map by name. */
type SpacePointVectorValues = number[] | number[][] | Record<string, SpaceVector>;

// The bridge answers `null` instead of `[]` when no point matches.
type PointsResult = { points: SpacePoint[] | null; next_page_offset?: PointId | null };

/** One condition of the `must` filter on a points listing. */
export type PointCondition =
  | { has_id: PointId[] }
  | { key: string; match: { value: string | number | bigint | boolean } | { text_any: string } }
  | { key: string; range: { gte: number; lte: number } };

// Where the next page starts: a point id for a scroll, a count for a recommendation. `null` is the first page.
type PageParam = PointId | null;

type PointsPage = { points: SpacePoint[]; next: PageParam };

/** What the points of a collection are searched by. */
export type SpacePointsSearch = {
  /** Recommends the points most like these, instead of listing in storage order. */
  similarTo: PointId[];
  /** The named vector the recommendation compares; the unnamed one when absent. */
  using?: string;
  conditions: PointCondition[];
};

type SpacePointsParams = SpaceCollectionParams & SpacePointsSearch;

type SpacePointParams = SpaceCollectionParams & { pointId: PointId };

// Under the collections key: dropping what is known of a space's collections drops their points too.
const spacePointsKey = ({ accountId, spaceId, baseUrl, collectionName }: SpaceCollectionParams) =>
  createQueryKey(...spaceCollectionsKey({ accountId, spaceId }), 'points', baseUrl, collectionName);

const pointsUrl = (baseUrl: string, collectionName: string, path: string) =>
  `${collectionUrl(baseUrl, collectionName)}/points/${path}`;

// Ids and payloads may hold integers past 2^53, which `JSON.stringify` refuses and `response.json()` rounds: bodies
// go out through `stringifyLosslessJson`, answers come back through the lossless middleware.
const pointsClient = (client: QueryClient, params: SpaceIds) =>
  spaceClient(client, params).middlewares([losslessJsonMiddleware()]).content('application/json');

export const spacePointsService = {
  /** Pages of a collection's points with their payload, filtered and optionally ranked by similarity. */
  listPointsQuery: ({ accountId, spaceId, baseUrl, collectionName, similarTo, using, conditions }: SpacePointsParams) =>
    infiniteQueryOptions({
      // A bigint id cannot be hashed into a query key: the search goes in as lossless JSON.
      queryKey: [
        ...spacePointsKey({ accountId, spaceId, baseUrl, collectionName }),
        stringifyLosslessJson({ similarTo, using, conditions }),
      ],
      initialPageParam: null as PageParam,
      queryFn: async ({ client, pageParam, signal }): Promise<PointsPage> => {
        const points = pointsClient(client, { accountId, spaceId }).options({ signal });
        const request = {
          limit: POINTS_PAGE_SIZE,
          filter: conditions.length > 0 ? { must: conditions } : undefined,
          // Vectors are left out: they make up most of a page's bytes, and a card reads its own on demand.
          with_payload: true,
        };

        if (similarTo.length === 0) {
          const result = await points
            .post(
              stringifyLosslessJson({ ...request, offset: pageParam ?? undefined }),
              pointsUrl(baseUrl, collectionName, 'scroll'),
            )
            .json(restResult<PointsResult>);
          return { points: result.points ?? [], next: result.next_page_offset ?? null };
        }

        // The query API pages by count: the next page skips the points already shown.
        const offset = Number(pageParam ?? 0);
        const result = await points
          .post(
            stringifyLosslessJson({ ...request, offset, query: { recommend: { positive: similarTo } }, using }),
            pointsUrl(baseUrl, collectionName, 'query'),
          )
          .json(restResult<PointsResult>);
        const page = result.points ?? [];
        return { points: page, next: page.length === POINTS_PAGE_SIZE ? offset + page.length : null };
      },
      // A recommendation resumes after the points still loaded, so one dropped from the cache is not skipped.
      getNextPageParam: (page, pages) => {
        if (page.next === null) {
          return undefined;
        }
        return similarTo.length > 0 ? pages.flatMap(({ points }) => points).length : page.next;
      },
    }),

  /** One vector of a point by name, or all of them without one; `null` when the point lacks it. */
  getPointVectorQuery: ({
    accountId,
    spaceId,
    baseUrl,
    collectionName,
    pointId,
    using,
  }: SpacePointParams & { using?: string }) =>
    queryOptions({
      // Apart from the points pages, which the point writes rewrite in place.
      queryKey: [
        ...spaceCollectionsKey({ accountId, spaceId }),
        'point-vector',
        baseUrl,
        collectionName,
        stringifyLosslessJson({ pointId, using }),
      ],
      queryFn: async ({ client, signal }) => {
        const [point] = await pointsClient(client, { accountId, spaceId })
          .options({ signal })
          .post(
            stringifyLosslessJson({ ids: [pointId], with_payload: false, with_vector: using === undefined || [using] }),
            `${collectionUrl(baseUrl, collectionName)}/points`,
          )
          .json(restResult<{ id: PointId; vector?: SpacePointVectorValues | null }[]>);
        const vectors = point?.vector ?? null;
        // A lone unnamed vector comes bare; beside named vectors it sits under the key "".
        if (using === undefined || vectors === null || Array.isArray(vectors)) {
          return vectors;
        }
        return vectors[using] ?? null;
      },
    }),
};

// Reads lag behind an applied write, so a refetch now could undo it: the cached pages take the write, and the
// searches refetch later, when the server re-evaluates filter membership.
async function updateCachedPoints(
  client: QueryClient,
  params: SpaceCollectionParams,
  update: (points: SpacePoint[]) => SpacePoint[],
) {
  const queryKey = spacePointsKey(params);
  // A read started before the write must not overwrite the updated pages when it finishes.
  await client.cancelQueries({ queryKey });

  client.setQueriesData<InfiniteData<PointsPage, PageParam>>(
    { queryKey },
    (data) => data && { ...data, pages: data.pages.map((page) => ({ ...page, points: update(page.points) })) },
  );
  return client.invalidateQueries({ queryKey, refetchType: 'none' });
}

/** Deletes a point, answering once the updater has applied it. */
export const useDeleteSpacePointMutation = () =>
  useMutation({
    mutationFn: ({ accountId, spaceId, baseUrl, collectionName, pointId }: SpacePointParams, { client }) =>
      pointsClient(client, { accountId, spaceId })
        .post(stringifyLosslessJson({ points: [pointId] }), pointsUrl(baseUrl, collectionName, 'delete?wait=true'))
        .res(),
    onSuccess: (_data, { pointId, ...params }, _onMutateResult, { client }) =>
      updateCachedPoints(client, params, (points) => points.filter(({ id }) => id !== pointId)),
  });

/** Replaces a point's whole payload, answering once the updater has applied it. */
export const useOverwriteSpacePointPayloadMutation = () =>
  useMutation({
    mutationFn: (
      {
        accountId,
        spaceId,
        baseUrl,
        collectionName,
        pointId,
        payload,
      }: SpacePointParams & { payload: SpacePointPayload },
      { client },
    ) =>
      pointsClient(client, { accountId, spaceId })
        .put(
          stringifyLosslessJson({ payload, points: [pointId] }),
          pointsUrl(baseUrl, collectionName, 'payload?wait=true'),
        )
        .res(),
    onSuccess: (_data, { pointId, payload, ...params }, _onMutateResult, { client }) =>
      updateCachedPoints(client, params, (points) =>
        points.map((point) => (point.id === pointId ? { ...point, payload } : point)),
      ),
  });
